import * as React from "react"
import { motion, AnimatePresence, MotionConfig } from "framer-motion"
import { ChevronDown, Home, Briefcase, Plane, DollarSign, Heart, Users, Layers, Grid3x3 } from "lucide-react"
import { cn } from "../../lib/utils"

// Custom hook for click outside detection
function useClickAway<T extends HTMLElement>(ref: React.RefObject<T | null>, handler: (event: MouseEvent | TouchEvent) => void) {
  React.useEffect(() => {
    const listener = (event: MouseEvent | TouchEvent) => {
      if (!ref.current || ref.current.contains(event.target as Node)) {
        return
      }
      handler(event)
    }
    document.addEventListener("mousedown", listener)
    document.addEventListener("touchstart", listener)
    return () => {
      document.removeEventListener("mousedown", listener)
      document.removeEventListener("touchstart", listener)
    }
  }, [ref, handler])
}

// Button component
interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "outline"
  children: React.ReactNode
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, children, ...props }, ref) => {
    return (
      <button
        ref={ref}
        className={cn(
          "inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
          "disabled:pointer-events-none disabled:opacity-50",
          variant === "outline" && "border border-neutral-700 bg-transparent",
          className
        )}
        {...props}
      >
        {children}
      </button>
    )
  }
)
Button.displayName = "Button"

// Types
interface AppCategory {
  id: string
  name: string
  color: string
  icon: string
}

interface DropdownCategory {
  id: string
  label: string
  icon: React.ElementType
  color: string
}

// Icon mapping from emoji to lucide-react icons
const iconMap: Record<string, React.ElementType> = {
  '🏠': Home,
  '💼': Briefcase,
  '✈️': Plane,
  '💰': DollarSign,
  '💪': Heart,
  '🤝': Users,
}

// Convert app categories to dropdown format
function convertCategories(appCategories: AppCategory[]): DropdownCategory[] {
  // Add Home as first option
  const homeOption: DropdownCategory = {
    id: "home",
    label: "Home",
    icon: Grid3x3,
    color: "#E07A5F",
  }
  
  const categoryOptions = appCategories.map(cat => ({
    id: cat.id,
    label: cat.name,
    icon: iconMap[cat.icon] || Layers,
    color: cat.color,
  }))
  
  return [homeOption, ...categoryOptions]
}

// Icon wrapper with animation
const IconWrapper = ({
  icon: Icon,
  isHovered,
  color,
}: { icon: React.ElementType; isHovered: boolean; color: string }) => (
  <motion.div 
    className="w-4 h-4 mr-2 relative" 
    initial={false} 
    animate={isHovered ? { scale: 1.2 } : { scale: 1 }}
  >
    <Icon className="w-4 h-4" />
    {isHovered && (
      <motion.div
        className="absolute inset-0"
        style={{ color }}
        initial={{ pathLength: 0, opacity: 0 }}
        animate={{ pathLength: 1, opacity: 1 }}
        transition={{ duration: 0.5, ease: "easeInOut" }}
      >
        <Icon className="w-4 h-4" strokeWidth={2} />
      </motion.div>
    )}
  </motion.div>
)

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      when: "beforeChildren",
      staggerChildren: 0.1,
    },
  },
}

const itemVariants = {
  hidden: { opacity: 0, y: -10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
      ease: "easeOut" as const,
    },
  },
}

// Main component
interface FluidDropdownProps {
  categories: AppCategory[]
  onCategorySelect: (categoryId: string) => void
  currentCategory: string | null
}

export function FluidDropdown({ categories: appCategories, onCategorySelect, currentCategory }: FluidDropdownProps) {
  const [isOpen, setIsOpen] = React.useState(false)
  const [hoveredCategory, setHoveredCategory] = React.useState<string | null>(null)
  const dropdownRef = React.useRef<HTMLDivElement>(null)

  const categories = convertCategories(appCategories)
  const selectedCategoryId = currentCategory || "home"
  const selectedCategory = categories.find(c => c.id === selectedCategoryId) || categories[0]

  useClickAway<HTMLDivElement>(dropdownRef, () => setIsOpen(false))

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setIsOpen(false)
    }
  }

  const handleCategoryClick = (categoryId: string) => {
    if (categoryId === "home") {
      // Navigate to home page - clear hash and category
      window.location.hash = ""
      setIsOpen(false)
      // If we're on a category page, scroll to categories section
      // Otherwise scroll to top
      if (currentCategory) {
        // We're on a category page, scroll to categories section
        setTimeout(() => {
          const categoriesSection = document.getElementById('categories')
          if (categoriesSection) {
            categoriesSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
          } else {
            // Fallback to top if categories section not found
            window.scrollTo({ top: 0, behavior: 'smooth' })
          }
        }, 100)
      } else {
        // Already on home, scroll to top
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
      // Clear the category selection
      onCategorySelect("")
    } else {
      onCategorySelect(categoryId)
      setIsOpen(false)
    }
  }

  return (
    <MotionConfig reducedMotion="user">
      <div
        className="w-full max-w-md relative"
        ref={dropdownRef}
      >
        <Button
          variant="outline"
          onClick={() => setIsOpen(!isOpen)}
          className={cn(
            "w-full justify-center bg-neutral-900/80 backdrop-blur-sm text-neutral-400",
            "hover:bg-neutral-800/90 hover:text-neutral-200",
            "focus:ring-2 focus:ring-neutral-700 focus:ring-offset-2 focus:ring-offset-black",
            "transition-all duration-200 ease-in-out",
            "border border-neutral-700/50 focus:border-neutral-600",
            "h-10 relative",
            isOpen && "bg-neutral-800/90 text-neutral-200",
          )}
          aria-expanded={isOpen}
          aria-haspopup="true"
        >
          <span className="flex items-center justify-center flex-1">
            <IconWrapper 
              icon={selectedCategory.icon} 
              isHovered={false} 
              color={selectedCategory.color} 
            />
            {selectedCategory.label}
          </span>
          <motion.div
            animate={{ rotate: isOpen ? 180 : 0 }}
            transition={{ duration: 0.2 }}
            className="flex items-center justify-center w-5 h-5 absolute right-3"
          >
            <ChevronDown className="w-4 h-4" />
          </motion.div>
        </Button>
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 1, y: 0, height: 0 }}
              animate={{
                opacity: 1,
                y: 0,
                height: "auto",
                transition: {
                  type: "spring",
                  stiffness: 500,
                  damping: 30,
                  mass: 1,
                },
              }}
              exit={{
                opacity: 0,
                y: 0,
                height: 0,
                transition: {
                  type: "spring",
                  stiffness: 500,
                  damping: 30,
                  mass: 1,
                },
              }}
              className="absolute left-0 right-0 top-full mt-2 z-50"
              onKeyDown={handleKeyDown}
            >
              <motion.div
                className="w-full rounded-lg border border-neutral-800/50 bg-neutral-900/95 backdrop-blur-md p-1 shadow-lg"
                initial={{ borderRadius: 8 }}
                animate={{
                  borderRadius: 12,
                  transition: { duration: 0.2 },
                }}
                style={{ transformOrigin: "top" }}
              >
                <motion.div 
                  className="py-2 relative" 
                  variants={containerVariants} 
                  initial="hidden" 
                  animate="visible"
                >
                    <motion.div
                      layoutId="hover-highlight"
                      className="absolute inset-x-1 bg-neutral-800/80 rounded-md"
                      animate={{
                        y: categories.findIndex((c) => (hoveredCategory || selectedCategoryId) === c.id) * 40 +
                          (categories.findIndex((c) => (hoveredCategory || selectedCategoryId) === c.id) > 0 ? 20 : 0),
                        height: 40,
                      }}
                      transition={{
                        type: "spring",
                        bounce: 0.15,
                        duration: 0.5,
                      }}
                    />
                  {categories.map((category, index) => (
                    <React.Fragment key={category.id}>
                      {index === 1 && (
                        <motion.div 
                          className="mx-4 my-2.5 border-t border-neutral-700/50" 
                          variants={itemVariants} 
                        />
                      )}
                      <motion.button
                        onClick={() => handleCategoryClick(category.id)}
                        onHoverStart={() => setHoveredCategory(category.id)}
                        onHoverEnd={() => setHoveredCategory(null)}
                        className={cn(
                          "relative flex w-full items-center px-4 py-2.5 text-sm rounded-md",
                          "transition-colors duration-150",
                          "focus:outline-none",
                          selectedCategoryId === category.id || hoveredCategory === category.id
                            ? "text-neutral-200"
                            : "text-neutral-400",
                        )}
                        whileTap={{ scale: 0.98 }}
                        variants={itemVariants}
                      >
                        <IconWrapper
                          icon={category.icon}
                          isHovered={hoveredCategory === category.id}
                          color={category.color}
                        />
                        {category.label}
                      </motion.button>
                    </React.Fragment>
                  ))}
                </motion.div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </MotionConfig>
  )
}

